import * as Yup from 'yup';

import {
  ApiEndpoint,
  columnDirection,
  columnSpace,
  ConditionOperatorType,
  ConditionVariableType,
  FormActionType,
  FormType,
  InputType,
  Translation,
} from '@enums';
import i18n from '@translations';
import type { Condition, DualListBoxProps, EndpointType, FormConfig, FormConfigMapping, OptionItem } from '@types';

interface GetWAFFormConfigParams {
  endpoint?: EndpointType;
  options?: OptionItem<string | number>[];
  schema?: Yup.AnySchema;
  isEdit?: boolean;
}

export const getWhiteListDualListBoxConfig = ({ options }): DualListBoxProps => {
  return {
    name: 'rulesets',
    options,
    leftPanel: {
      title: i18n.t(`${Translation.Configuration}:forms.waf.whiteList.wafRules`),
      fieldName: 'fullRuleSets',
      placeholder: i18n.t(`${Translation.Configuration}:forms.waf.whiteList.searchByRuleName`),
    },
    rightPanel: {
      title: i18n.t(`${Translation.Configuration}:forms.waf.whiteList.skipWafRules`),
      fieldName: 'skipRulesets',
      placeholder: i18n.t(`${Translation.Configuration}:forms.waf.whiteList.searchByRuleName`),
    },
    direction: columnDirection.Row,
    showValue: false,
    label: i18n.t(`${Translation.Configuration}:forms.waf.whiteList.skipWafRules`),
    required: true,
    titleFontSize: 5,
    labelClassName: 'text-wrap text-left',
  };
};

export const getWAFWhiteListFormConfig = ({
  endpoint = ApiEndpoint.WHITELIST,
  schema,
  isEdit = false,
}: GetWAFFormConfigParams): FormConfig => {
  const tForm: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:forms.waf.${key}`);

  const emptyData: { main: Condition } = {
    main: {
      field: ConditionVariableType.URI,
      argument: '',
      operator: ConditionOperatorType.stringEqual,
      values: [{ value: '' }],
    },
  };

  const WHITELIST_FORM: FormConfigMapping = {
    [ApiEndpoint.WHITELIST]: {
      formType: FormType.CdnWAF,
      endpoint: ApiEndpoint.WHITELIST,
      formActionType: isEdit ? FormActionType.Edit : FormActionType.Create,
      label: {
        title: i18n.t(`${Translation.Configuration}:modals.waf.${isEdit ? 'editWhiteList' : 'addNewWhiteList'}`),
        description: i18n.t(
          `${Translation.Configuration}:modals.waf.${isEdit ? 'editWhiteListDescription' : 'addNewWhiteListDescription'}`,
        ),
        confirmButton: i18n.t(
          `${Translation.Configuration}:buttons.waf.${isEdit ? 'editWhiteList' : 'addNewWhiteList'}`,
        ),
      },
      inputList: [
        {
          name: 'name',
          defaultValue: '',
          label: i18n.t(`${Translation.Configuration}:forms.waf.whiteList.wafWhiteListName`),
          placeholder: i18n.t(`${Translation.Configuration}:forms.waf.whiteList.wafWhiteListNamePlaceHolder`),
          required: true,
          direction: columnDirection.Row,
        },
        {
          name: 'conditions',
          defaultValue: '',
          placeholder: '',
          inputType: InputType.Table,
          direction: columnDirection.Column,
          columnPercentage: columnSpace.TwoOverFive,
          format: tForm,
          fullWidth: true,
          required: false,
          emptyData,
        },
      ],
      validationSchema: schema,
    },
  };

  return (WHITELIST_FORM[endpoint] ?? WHITELIST_FORM[ApiEndpoint.WHITELIST]) as FormConfig;
};
